import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  MoreVertical, 
  Trash2, 
  Edit2, 
  Train, 
  Plane, 
  Hotel,
  Download,
  FileText,
  Loader2,
  ArrowLeft,
  Users,
  Calendar,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { getBookings } from '../lib/api';

// Booking Details API functions
const getBookingDetails = (bookingId) => api.get(`/booking-details?booking_id=${bookingId}`);
const createTrainBooking = (data) => api.post('/booking-details/train', data);
const updateTrainBooking = (id, data) => api.put(`/booking-details/train/${id}`, data);
const deleteTrainBooking = (id) => api.delete(`/booking-details/train/${id}`);
const createFlightBooking = (data) => api.post('/booking-details/flight', data);
const updateFlightBooking = (id, data) => api.put(`/booking-details/flight/${id}`, data);
const deleteFlightBooking = (id) => api.delete(`/booking-details/flight/${id}`);
const createRoomReservation = (data) => api.post('/booking-details/room', data);
const updateRoomReservation = (id, data) => api.put(`/booking-details/room/${id}`, data);
const deleteRoomReservation = (id) => api.delete(`/booking-details/room/${id}`);
const exportBookingDetailsPdf = (bookingId) => api.get(`/booking-details/export/pdf?booking_id=${bookingId}`, { responseType: 'blob' });

export const BookingDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingIdFromUrl = searchParams.get('booking_id');
  
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState(bookingIdFromUrl || '');
  const [bookingInfo, setBookingInfo] = useState(null);
  const [trainData, setTrainData] = useState([]);
  const [flightData, setFlightData] = useState([]);
  const [roomData, setRoomData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('train');
  
  // Modal states
  const [trainModalOpen, setTrainModalOpen] = useState(false);
  const [flightModalOpen, setFlightModalOpen] = useState(false);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Form states
  const [trainForm, setTrainForm] = useState({
    passenger_name: '', age: '', gender: '', seat_type: '',
    boarding_from: '', boarding_to: '', train_name: '', train_number: '',
    departure_date: '', departure_time: '', pnr_number: '', ticket_file: '', notes: ''
  });
  
  const [flightForm, setFlightForm] = useState({
    passenger_name: '', age: '', gender: '', dob: '', passport_number: '',
    boarding_from: '', boarding_to: '', airline_name: '', flight_number: '',
    departure_date: '', departure_time: '', arrival_time: '', pnr_number: '', ticket_file: '', notes: ''
  });
  
  const [roomForm, setRoomForm] = useState({
    hotel_name: '', room_type: '', room_number: '', number_of_guests: 1,
    check_in_date: '', check_out_date: '', passenger_names: '',
    confirmation_number: '', hotel_contact: '', hotel_address: '', special_requests: '', notes: ''
  });

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    if (selectedBookingId) {
      loadBookingDetails();
    }
  }, [selectedBookingId]);

  const loadBookings = async () => {
    try {
      const { data } = await getBookings();
      setBookings(data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadBookingDetails = async () => {
    if (!selectedBookingId) return;
    setLoading(true);
    try {
      const { data } = await getBookingDetails(selectedBookingId);
      setBookingInfo(data.booking);
      setTrainData(data.train_bookings || []);
      setFlightData(data.flight_bookings || []);
      setRoomData(data.room_reservations || []);
    } catch (error) {
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const resetTrainForm = () => {
    setTrainForm({
      passenger_name: '', age: '', gender: '', seat_type: '',
      boarding_from: '', boarding_to: '', train_name: '', train_number: '',
      departure_date: '', departure_time: '', pnr_number: '', ticket_file: '', notes: ''
    });
    setEditingItem(null);
  };

  const resetFlightForm = () => {
    setFlightForm({
      passenger_name: '', age: '', gender: '', dob: '', passport_number: '',
      boarding_from: '', boarding_to: '', airline_name: '', flight_number: '',
      departure_date: '', departure_time: '', arrival_time: '', pnr_number: '', ticket_file: '', notes: ''
    });
    setEditingItem(null);
  };

  const resetRoomForm = () => {
    setRoomForm({
      hotel_name: '', room_type: '', room_number: '', number_of_guests: 1,
      check_in_date: '', check_out_date: '', passenger_names: '',
      confirmation_number: '', hotel_contact: '', hotel_address: '', special_requests: '', notes: ''
    });
    setEditingItem(null);
  };

  // Helper to sanitize form data - convert empty strings to null for optional fields
  const sanitizePayload = (data, intFields = []) => {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      if (value === '' || value === undefined) {
        sanitized[key] = null;
      } else if (intFields.includes(key)) {
        sanitized[key] = value === '' || value === null ? null : parseInt(value, 10);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  };

  // Train CRUD
  const handleTrainSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = sanitizePayload({ ...trainForm, booking_id: selectedBookingId }, ['age']);
      if (editingItem) {
        await updateTrainBooking(editingItem.id, payload);
        toast.success('Train booking updated');
      } else {
        await createTrainBooking(payload);
        toast.success('Train booking added');
      }
      setTrainModalOpen(false);
      resetTrainForm();
      loadBookingDetails();
    } catch (error) {
      toast.error('Failed to save train booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTrain = (item) => {
    setTrainForm({
      passenger_name: item.passenger_name || '',
      age: item.age || '',
      gender: item.gender || '',
      seat_type: item.seat_type || '',
      boarding_from: item.boarding_from || '',
      boarding_to: item.boarding_to || '',
      train_name: item.train_name || '',
      train_number: item.train_number || '',
      departure_date: item.departure_date || '',
      departure_time: item.departure_time || '',
      pnr_number: item.pnr_number || '',
      ticket_file: item.ticket_file || '',
      notes: item.notes || ''
    });
    setEditingItem(item);
    setTrainModalOpen(true);
  };

  const handleDeleteTrain = async (id) => {
    if (!window.confirm('Delete this train booking?')) return;
    try {
      await deleteTrainBooking(id);
      toast.success('Train booking deleted');
      loadBookingDetails();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Flight CRUD
  const handleFlightSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = sanitizePayload({ ...flightForm, booking_id: selectedBookingId }, ['age']);
      if (editingItem) {
        await updateFlightBooking(editingItem.id, payload);
        toast.success('Flight booking updated');
      } else {
        await createFlightBooking(payload);
        toast.success('Flight booking added');
      }
      setFlightModalOpen(false);
      resetFlightForm();
      loadBookingDetails();
    } catch (error) {
      toast.error('Failed to save flight booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditFlight = (item) => {
    setFlightForm({
      passenger_name: item.passenger_name || '',
      age: item.age || '',
      gender: item.gender || '',
      dob: item.dob || '',
      passport_number: item.passport_number || '',
      boarding_from: item.boarding_from || '',
      boarding_to: item.boarding_to || '',
      airline_name: item.airline_name || '',
      flight_number: item.flight_number || '',
      departure_date: item.departure_date || '',
      departure_time: item.departure_time || '',
      arrival_time: item.arrival_time || '',
      pnr_number: item.pnr_number || '',
      ticket_file: item.ticket_file || '',
      notes: item.notes || ''
    });
    setEditingItem(item);
    setFlightModalOpen(true);
  };

  const handleDeleteFlight = async (id) => {
    if (!window.confirm('Delete this flight booking?')) return;
    try {
      await deleteFlightBooking(id);
      toast.success('Flight booking deleted');
      loadBookingDetails();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Room CRUD
  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = sanitizePayload({ ...roomForm, booking_id: selectedBookingId }, ['number_of_guests']);
      if (editingItem) {
        await updateRoomReservation(editingItem.id, payload);
        toast.success('Room reservation updated');
      } else {
        await createRoomReservation(payload);
        toast.success('Room reservation added');
      }
      setRoomModalOpen(false);
      resetRoomForm();
      loadBookingDetails();
    } catch (error) {
      toast.error('Failed to save room reservation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRoom = (item) => {
    setRoomForm({
      hotel_name: item.hotel_name || '',
      room_type: item.room_type || '',
      room_number: item.room_number || '',
      number_of_guests: item.number_of_guests || 1,
      check_in_date: item.check_in_date || '',
      check_out_date: item.check_out_date || '',
      passenger_names: item.passenger_names || '',
      confirmation_number: item.confirmation_number || '',
      hotel_contact: item.hotel_contact || '',
      hotel_address: item.hotel_address || '',
      special_requests: item.special_requests || '',
      notes: item.notes || ''
    });
    setEditingItem(item);
    setRoomModalOpen(true);
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm('Delete this room reservation?')) return;
    try {
      await deleteRoomReservation(id);
      toast.success('Room reservation deleted');
      loadBookingDetails();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Export PDF
  const handleExportPdf = async () => {
    if (!selectedBookingId) return;
    setExporting(true);
    try {
      const response = await exportBookingDetailsPdf(selectedBookingId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BookingDetails_${selectedBookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div data-testid="booking-details-container" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/bookings')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>
              Booking Details Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage train, flight bookings and room reservations
            </p>
          </div>
        </div>
        {selectedBookingId && (
          <Button 
            data-testid="export-pdf-btn"
            onClick={handleExportPdf} 
            disabled={exporting}
            className="text-white"
            style={{ backgroundColor: '#383838' }}
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export PDF for Tour Operator
          </Button>
        )}
      </div>

      {/* Booking Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="whitespace-nowrap">Select Booking:</Label>
              <Select value={selectedBookingId} onValueChange={setSelectedBookingId}>
                <SelectTrigger className="w-80">
                  <SelectValue placeholder="-- Select a Booking --" />
                </SelectTrigger>
                <SelectContent>
                  {bookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      #{b.id?.slice(-6)} - {b.destination} ({b.customer_name || 'N/A'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bookingInfo && (
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {bookingInfo.destination}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {formatDate(bookingInfo.travel_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {bookingInfo.travelers || 1} travelers
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!selectedBookingId ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Select a Booking</h3>
            <p className="text-muted-foreground text-center">
              Choose a booking from the dropdown above to manage train, flight, and room details.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-100">
            <TabsTrigger data-testid="tab-train" value="train" className="data-[state=active]:bg-white">
              <Train className="w-4 h-4 mr-2" /> Train Bookings
            </TabsTrigger>
            <TabsTrigger data-testid="tab-flight" value="flight" className="data-[state=active]:bg-white">
              <Plane className="w-4 h-4 mr-2" /> Flight Bookings
            </TabsTrigger>
            <TabsTrigger data-testid="tab-room" value="room" className="data-[state=active]:bg-white">
              <Hotel className="w-4 h-4 mr-2" /> Room Reservations
            </TabsTrigger>
          </TabsList>

          {/* Train Tab */}
          <TabsContent value="train" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#84bd0e' }}>
                  <Train className="w-5 h-5" /> Train Bookings
                </CardTitle>
                <Button data-testid="add-train-btn" onClick={() => { resetTrainForm(); setTrainModalOpen(true); }} className="text-white" style={{ backgroundColor: '#383838' }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Passenger
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {trainData.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>S.No</TableHead>
                        <TableHead>Passenger Name</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>Gender</TableHead>
                        <TableHead>Seat Type</TableHead>
                        <TableHead>From - To</TableHead>
                        <TableHead>Train</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>PNR</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trainData.map((t, i) => (
                        <TableRow key={t.id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell className="font-medium">{t.passenger_name}</TableCell>
                          <TableCell>{t.age || '-'}</TableCell>
                          <TableCell className="capitalize">{t.gender || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="uppercase">{t.seat_type || '-'}</Badge>
                          </TableCell>
                          <TableCell>{t.boarding_from} → {t.boarding_to}</TableCell>
                          <TableCell>{t.train_name || '-'} {t.train_number ? `(${t.train_number})` : ''}</TableCell>
                          <TableCell>{formatDate(t.departure_date)}</TableCell>
                          <TableCell>{t.pnr_number || '-'}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditTrain(t)}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteTrain(t.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Train className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No train bookings added yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Flight Tab */}
          <TabsContent value="flight" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#84bd0e' }}>
                  <Plane className="w-5 h-5" /> Flight Bookings
                </CardTitle>
                <Button data-testid="add-flight-btn" onClick={() => { resetFlightForm(); setFlightModalOpen(true); }} className="text-white" style={{ backgroundColor: '#383838' }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Passenger
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {flightData.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>S.No</TableHead>
                        <TableHead>Passenger Name</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>DOB</TableHead>
                        <TableHead>Passport</TableHead>
                        <TableHead>From - To</TableHead>
                        <TableHead>Airline/Flight</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>PNR</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flightData.map((f, i) => (
                        <TableRow key={f.id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell className="font-medium">{f.passenger_name}</TableCell>
                          <TableCell>{f.age || '-'}</TableCell>
                          <TableCell>{formatDate(f.dob)}</TableCell>
                          <TableCell>{f.passport_number || '-'}</TableCell>
                          <TableCell>{f.boarding_from} → {f.boarding_to}</TableCell>
                          <TableCell>{f.airline_name || '-'} {f.flight_number ? `(${f.flight_number})` : ''}</TableCell>
                          <TableCell>{formatDate(f.departure_date)}</TableCell>
                          <TableCell>{f.pnr_number || '-'}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditFlight(f)}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteFlight(f.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Plane className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No flight bookings added yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Room Tab */}
          <TabsContent value="room" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#84bd0e' }}>
                  <Hotel className="w-5 h-5" /> Room Reservations
                </CardTitle>
                <Button data-testid="add-room-btn" onClick={() => { resetRoomForm(); setRoomModalOpen(true); }} className="text-white" style={{ backgroundColor: '#383838' }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Room
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {roomData.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>S.No</TableHead>
                        <TableHead>Hotel Name</TableHead>
                        <TableHead>Room Type</TableHead>
                        <TableHead>Room No.</TableHead>
                        <TableHead>Check-in</TableHead>
                        <TableHead>Check-out</TableHead>
                        <TableHead>Guests</TableHead>
                        <TableHead>Assigned Passengers</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roomData.map((r, i) => (
                        <TableRow key={r.id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell className="font-medium">{r.hotel_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">{r.room_type || '-'}</Badge>
                          </TableCell>
                          <TableCell>{r.room_number || '-'}</TableCell>
                          <TableCell>{formatDate(r.check_in_date)}</TableCell>
                          <TableCell>{formatDate(r.check_out_date)}</TableCell>
                          <TableCell>{r.number_of_guests || 1}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{r.passenger_names || '-'}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditRoom(r)}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteRoom(r.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Hotel className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No room reservations added yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Train Modal */}
      <Dialog open={trainModalOpen} onOpenChange={setTrainModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Train className="w-5 h-5" /> {editingItem ? 'Edit Train Booking' : 'Add Train Booking'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTrainSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Passenger Name *</Label>
                <Input value={trainForm.passenger_name} onChange={(e) => setTrainForm({...trainForm, passenger_name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Age</Label>
                <Input type="number" min="1" max="120" value={trainForm.age} onChange={(e) => setTrainForm({...trainForm, age: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={trainForm.gender} onValueChange={(v) => setTrainForm({...trainForm, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Seat Type</Label>
                <Select value={trainForm.seat_type} onValueChange={(v) => setTrainForm({...trainForm, seat_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sleeper">Sleeper</SelectItem>
                    <SelectItem value="3ac">3AC</SelectItem>
                    <SelectItem value="2ac">2AC</SelectItem>
                    <SelectItem value="1ac">1AC</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Boarding From *</Label>
                <Input value={trainForm.boarding_from} onChange={(e) => setTrainForm({...trainForm, boarding_from: e.target.value})} placeholder="e.g., Chennai Central" required />
              </div>
              <div className="space-y-2">
                <Label>Boarding To *</Label>
                <Input value={trainForm.boarding_to} onChange={(e) => setTrainForm({...trainForm, boarding_to: e.target.value})} placeholder="e.g., Mumbai CST" required />
              </div>
              <div className="space-y-2">
                <Label>Train Name</Label>
                <Input value={trainForm.train_name} onChange={(e) => setTrainForm({...trainForm, train_name: e.target.value})} placeholder="e.g., Chennai Express" />
              </div>
              <div className="space-y-2">
                <Label>Train Number</Label>
                <Input value={trainForm.train_number} onChange={(e) => setTrainForm({...trainForm, train_number: e.target.value})} placeholder="e.g., 12163" />
              </div>
              <div className="space-y-2">
                <Label>Departure Date</Label>
                <Input type="date" value={trainForm.departure_date} onChange={(e) => setTrainForm({...trainForm, departure_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Departure Time</Label>
                <Input type="time" value={trainForm.departure_time} onChange={(e) => setTrainForm({...trainForm, departure_time: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>PNR Number</Label>
                <Input value={trainForm.pnr_number} onChange={(e) => setTrainForm({...trainForm, pnr_number: e.target.value})} placeholder="e.g., 1234567890" />
              </div>
              <div className="space-y-2">
                <Label>Ticket File URL</Label>
                <Input type="url" value={trainForm.ticket_file} onChange={(e) => setTrainForm({...trainForm, ticket_file: e.target.value})} placeholder="https://..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={trainForm.notes} onChange={(e) => setTrainForm({...trainForm, notes: e.target.value})} rows={2} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setTrainModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingItem ? 'Update' : 'Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Flight Modal */}
      <Dialog open={flightModalOpen} onOpenChange={setFlightModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Plane className="w-5 h-5" /> {editingItem ? 'Edit Flight Booking' : 'Add Flight Booking'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFlightSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Passenger Name *</Label>
                <Input value={flightForm.passenger_name} onChange={(e) => setFlightForm({...flightForm, passenger_name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Age</Label>
                <Input type="number" min="1" max="120" value={flightForm.age} onChange={(e) => setFlightForm({...flightForm, age: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={flightForm.gender} onValueChange={(v) => setFlightForm({...flightForm, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" value={flightForm.dob} onChange={(e) => setFlightForm({...flightForm, dob: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Passport Number</Label>
                <Input value={flightForm.passport_number} onChange={(e) => setFlightForm({...flightForm, passport_number: e.target.value})} placeholder="e.g., A1234567" />
              </div>
              <div className="space-y-2">
                <Label>Boarding From *</Label>
                <Input value={flightForm.boarding_from} onChange={(e) => setFlightForm({...flightForm, boarding_from: e.target.value})} placeholder="e.g., Chennai (MAA)" required />
              </div>
              <div className="space-y-2">
                <Label>Boarding To *</Label>
                <Input value={flightForm.boarding_to} onChange={(e) => setFlightForm({...flightForm, boarding_to: e.target.value})} placeholder="e.g., Dubai (DXB)" required />
              </div>
              <div className="space-y-2">
                <Label>Airline Name</Label>
                <Input value={flightForm.airline_name} onChange={(e) => setFlightForm({...flightForm, airline_name: e.target.value})} placeholder="e.g., Emirates" />
              </div>
              <div className="space-y-2">
                <Label>Flight Number</Label>
                <Input value={flightForm.flight_number} onChange={(e) => setFlightForm({...flightForm, flight_number: e.target.value})} placeholder="e.g., EK543" />
              </div>
              <div className="space-y-2">
                <Label>Departure Date</Label>
                <Input type="date" value={flightForm.departure_date} onChange={(e) => setFlightForm({...flightForm, departure_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Departure Time</Label>
                <Input type="time" value={flightForm.departure_time} onChange={(e) => setFlightForm({...flightForm, departure_time: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Arrival Time</Label>
                <Input type="time" value={flightForm.arrival_time} onChange={(e) => setFlightForm({...flightForm, arrival_time: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>PNR Number</Label>
                <Input value={flightForm.pnr_number} onChange={(e) => setFlightForm({...flightForm, pnr_number: e.target.value})} placeholder="e.g., ABC123" />
              </div>
              <div className="space-y-2">
                <Label>Ticket File URL</Label>
                <Input type="url" value={flightForm.ticket_file} onChange={(e) => setFlightForm({...flightForm, ticket_file: e.target.value})} placeholder="https://..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={flightForm.notes} onChange={(e) => setFlightForm({...flightForm, notes: e.target.value})} rows={2} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFlightModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingItem ? 'Update' : 'Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Room Modal */}
      <Dialog open={roomModalOpen} onOpenChange={setRoomModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Hotel className="w-5 h-5" /> {editingItem ? 'Edit Room Reservation' : 'Add Room Reservation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRoomSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hotel Name *</Label>
                <Input value={roomForm.hotel_name} onChange={(e) => setRoomForm({...roomForm, hotel_name: e.target.value})} placeholder="e.g., Taj Hotel" required />
              </div>
              <div className="space-y-2">
                <Label>Room Type</Label>
                <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({...roomForm, room_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="double">Double</SelectItem>
                    <SelectItem value="twin">Twin</SelectItem>
                    <SelectItem value="suite">Suite</SelectItem>
                    <SelectItem value="deluxe">Deluxe</SelectItem>
                    <SelectItem value="family">Family Room</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Room Number</Label>
                <Input value={roomForm.room_number} onChange={(e) => setRoomForm({...roomForm, room_number: e.target.value})} placeholder="e.g., 301" />
              </div>
              <div className="space-y-2">
                <Label>Number of Guests</Label>
                <Input type="number" min="1" value={roomForm.number_of_guests} onChange={(e) => setRoomForm({...roomForm, number_of_guests: parseInt(e.target.value) || 1})} />
              </div>
              <div className="space-y-2">
                <Label>Check-in Date</Label>
                <Input type="date" value={roomForm.check_in_date} onChange={(e) => setRoomForm({...roomForm, check_in_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Check-out Date</Label>
                <Input type="date" value={roomForm.check_out_date} onChange={(e) => setRoomForm({...roomForm, check_out_date: e.target.value})} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Passenger Names (Assigned to this room)</Label>
                <Textarea value={roomForm.passenger_names} onChange={(e) => setRoomForm({...roomForm, passenger_names: e.target.value})} rows={2} placeholder="e.g., John Doe, Jane Doe" />
              </div>
              <div className="space-y-2">
                <Label>Confirmation Number</Label>
                <Input value={roomForm.confirmation_number} onChange={(e) => setRoomForm({...roomForm, confirmation_number: e.target.value})} placeholder="e.g., CONF123456" />
              </div>
              <div className="space-y-2">
                <Label>Hotel Contact</Label>
                <Input value={roomForm.hotel_contact} onChange={(e) => setRoomForm({...roomForm, hotel_contact: e.target.value})} placeholder="e.g., +91 9876543210" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Hotel Address</Label>
                <Textarea value={roomForm.hotel_address} onChange={(e) => setRoomForm({...roomForm, hotel_address: e.target.value})} rows={2} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Special Requests</Label>
                <Textarea value={roomForm.special_requests} onChange={(e) => setRoomForm({...roomForm, special_requests: e.target.value})} rows={2} placeholder="e.g., Extra bed, Sea view, etc." />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setRoomModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingItem ? 'Update' : 'Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
